import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePWA } from './usePWA';

export const useEnhancedAuth = () => {
  const auth = useAuth();
  const { isInstalled, isMobile } = usePWA();

  const [rememberDevice, setRememberDevice] = useState(false);
  const [hasValidSession, setHasValidSession] = useState(false);

  // Check for valid session on mount and when user changes
  useEffect(() => {
    const checkSession = async () => {
      try {
        const user = await auth.checkAuth();
        setHasValidSession(!!user);
      } catch (error) {
        setHasValidSession(false);
      }
    };

    checkSession();
  }, [auth.user]); // Re-check when user state changes

  // Enhanced login with device memory
  const enhancedLogin = useCallback(async (email, password, options = {}) => {
    const {
      rememberMe = false,
      rememberDevice: rememberThisDevice = false
    } = options;

    try {
      // Standard login first
      const result = await auth.login(email, password, rememberMe);
      
      if (result.success) {
        // If login successful and on mobile/PWA, store device preferences
        if ((isMobile || isInstalled) && rememberThisDevice) {
          setRememberDevice(true);
          localStorage.setItem('device-remembered', 'true');
          localStorage.setItem('last-login-email', email);
        }
        setHasValidSession(true);
      }
      
      return result;
    } catch (error) {
      console.error('Enhanced login error:', error);
      return { success: false, error: error.message };
    }
  }, [auth, isMobile, isInstalled]);

  // Quick login for PWA (uses stored session if available)
  const quickLogin = useCallback(async () => {
    if (!isInstalled && !isMobile) {
      return { success: false, error: 'Quick login only available on mobile/PWA' };
    }

    try {
      // Check existing session
      const user = await auth.checkAuth();
      if (user) {
        setHasValidSession(true);
        return { success: true, method: 'session', user: user };
      }

      setHasValidSession(false);
      return { success: false, error: 'No valid session found' };
    } catch (error) {
      console.error('Quick login error:', error);
      setHasValidSession(false);
      return { success: false, error: error.message };
    }
  }, [isInstalled, isMobile, auth]);

  // Enhanced logout that clears device-specific data
  const enhancedLogout = useCallback(async (clearDeviceData = false) => {
    try {
      await auth.logout();
      setHasValidSession(false); // Always clear session state on logout
      
      if (clearDeviceData) {
        localStorage.removeItem('device-remembered');
        localStorage.removeItem('last-login-email');
        setRememberDevice(false);
      }
    } catch (error) {
      console.error('Enhanced logout error:', error);
    }
  }, [auth]);

  // Check if device is remembered
  useEffect(() => {
    const remembered = localStorage.getItem('device-remembered') === 'true';
    setRememberDevice(remembered);
  }, []);

  const enhancedGoogleSsoLogin = useCallback(async (credential) => {
    try {
      const result = await auth.googleSsoLogin(credential);
      if (result.success && (isMobile || isInstalled)) {
        setRememberDevice(true);
        localStorage.setItem('device-remembered', 'true');
        if (result.user?.email) {
          localStorage.setItem('last-login-email', result.user.email);
        }
      }
      if (result.success) {
        setHasValidSession(true);
      }
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [auth, isMobile, isInstalled]);

  return {
    ...auth,
    enhancedLogin,
    quickLogin,
    enhancedLogout,
    enhancedGoogleSsoLogin,
    rememberDevice,
    setRememberDevice,
    canUseQuickLogin: (isMobile || isInstalled) && rememberDevice && hasValidSession,
    lastLoginEmail: localStorage.getItem('last-login-email')
  };
}; 